import { inject, Injectable, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, filter, firstValueFrom, Observable, take } from 'rxjs';
import { CopilotActiveRun, MessageRole } from '../models/copilot.models';
import { CopilotActions } from '../store/copilot.actions';
import { CopilotState } from '../store/copilot.reducer';
import {
  selectedClient,
  selectedClientId,
  selectedConversationId,
  selectHighlightedClientIds,
  selectHighlightedConversations,
  selectActiveRunForConversation,
  selectAgentIdentifierForSelectedConversation,
  selectIsStreaming,
  selectMessagesByConversation,
  selectMessagesForSelectedConversation,
  selectSelectedConversation,
} from '../store/copilot.selectors';

@Injectable({ providedIn: 'root' })
export class CopilotFacadeService {
  private readonly store = inject(Store<CopilotState>);

  readonly selectedClientId$ = this.store.select(selectedClientId);
  readonly selectedClient$ = this.store.select(selectedClient);
  readonly selectedConversation$ = this.store.select(selectedConversationId);
  readonly selectedConversationRow$ = this.store.select(
    selectSelectedConversation,
  );
  readonly selectedAgentIdentifier$ = this.store.select(
    selectAgentIdentifierForSelectedConversation,
  );
  readonly messagesForSelectedConversation$ = this.store.select(
    selectMessagesForSelectedConversation,
  );
  readonly highlightedClientIds$ = this.store.select(
    selectHighlightedClientIds,
  );
  readonly highlightedConversations$ = this.store.select(
    selectHighlightedConversations,
  );
  private readonly clearContextValue = signal(0);
  readonly clearContextSignal = this.clearContextValue.asReadonly();

  private readonly panelOpenRequest = signal(0);
  readonly panelOpenRequested = this.panelOpenRequest.asReadonly();

  readonly disableInput = signal(false);
  readonly hideInput = signal(false);

  isStreaming$(conversationId: string): Observable<boolean> {
    return this.store.select(selectIsStreaming(conversationId));
  }

  activeRun$(conversationId: string): Observable<CopilotActiveRun | null> {
    return this.store.select(selectActiveRunForConversation(conversationId));
  }

  selectClient(clientId: string): void {
    this.store.dispatch(CopilotActions.selectClient({ clientId }));
    this.store.dispatch(CopilotActions.clearClientHighlight({ clientId }));
  }

  selectConversation(
    clientId: string,
    conversationId: string,
    view?: 'active' | 'archived',
  ): void {
    this.selectClient(clientId);
    this.store.dispatch(
      CopilotActions.selectConversation({ clientId, conversationId, view }),
    );
  }

  setConversationContext(
    clientId: string,
    conversationId: string,
    clientGuid: string,
  ): void {
    this.store.dispatch(
      CopilotActions.setConversationContext({
        clientId,
        conversationId,
        clientGuid,
      }),
    );
  }

  loadMessages(
    clientId: string,
    conversationId: string,
    forceRefresh = false,
  ): void {
    this.store.dispatch(
      CopilotActions.loadMessages({ clientId, conversationId, forceRefresh }),
    );
  }

  sendMessage(
    clientId: string,
    conversationId: string,
    content: string,
    legacyFileNameOrOwnerId?: string,
    legacyOwnerId?: string,
    clientGuid?: string,
  ): void {
    const fromExistingUiSessionCaller =
      legacyFileNameOrOwnerId === undefined && legacyOwnerId !== undefined;
    const ownerId = legacyOwnerId ?? legacyFileNameOrOwnerId;
    const requestClientGuid =
      clientGuid ?? (fromExistingUiSessionCaller ? content : undefined);
    this.store
      .select(selectIsStreaming(conversationId))
      .pipe(take(1))
      .subscribe((busy) => {
        if (busy) return;
        this.dispatchMessage(
          clientId,
          conversationId,
          content,
          ownerId,
          requestClientGuid,
        );
      });
  }

  async sendMessageToSelectedConversation(
    content: string,
    clientGuid?: string,
  ): Promise<boolean> {
    if (this.disableInput()) return false;

    const [clientId, conversationId] = await firstValueFrom(
      combineLatest([this.selectedClientId$, this.selectedConversation$]).pipe(
        take(1),
      ),
    );
    if (!clientId || !conversationId || !content.trim()) return false;

    const busy = await firstValueFrom(
      this.isStreaming$(conversationId).pipe(take(1)),
    );
    if (busy) return false;

    this.dispatchMessage(
      clientId,
      conversationId,
      content.trim(),
      undefined,
      clientGuid,
    );
    this.panelOpenRequest.update((value) => value + 1);
    return true;
  }

  clearClient(clientId: string): void {
    this.store.dispatch(CopilotActions.clearClient({ clientId }));
  }

  archiveConversation(clientId: string, conversationId: string): void {
    this.store.dispatch(
      CopilotActions.archiveConversation({ clientId, conversationId }),
    );
  }

  closeConversation(clientId: string, conversationId: string): void {
    this.store.dispatch(
      CopilotActions.closeConversation({ clientId, conversationId }),
    );
  }

  loadAndSelectConversation(
    clientId: string,
    conversationId: string,
    view: 'active' | 'archived' = 'active',
    forceRefresh = false,
    clientGuid?: string,
  ): void {
    if (clientGuid) {
      this.setConversationContext(clientId, conversationId, clientGuid);
    }
    this.loadMessages(clientId, conversationId, forceRefresh);
    this.store
      .select(selectMessagesByConversation(clientId, conversationId))
      .pipe(
        filter((messages) => Array.isArray(messages)),
        take(1),
      )
      .subscribe(() => this.selectConversation(clientId, conversationId, view));
  }

  continueReply(clientId: string, conversationId: string): void {
    this.loadMessages(clientId, conversationId, true);
  }

  triggerClientHighlight(clientId: string): void {
    this.store.dispatch(CopilotActions.triggerClientHighlight({ clientId }));
  }

  clearClientHighlight(clientId: string): void {
    this.store.dispatch(CopilotActions.clearClientHighlight({ clientId }));
  }

  clearConversationHighlight(clientId: string, conversationId: string): void {
    this.store.dispatch(
      CopilotActions.clearConversationHighlight({ clientId, conversationId }),
    );
  }

  clearContext(): void {
    this.selectedClientId$.pipe(take(1)).subscribe((clientId) => {
      if (clientId) this.selectConversation(clientId, '');
    });
    this.clearContextValue.update((value) => value + 1);
  }

  private dispatchMessage(
    clientId: string,
    conversationId: string,
    content: string,
    ownerId?: string,
    clientGuid?: string,
  ): void {
    const optimisticUserId = `temp-user-${uuid()}`;
    this.store.dispatch(
      CopilotActions.messageReceived({
        clientId,
        conversationId,
        message: {
          id: optimisticUserId,
          conversation_id: conversationId,
          role: MessageRole.USER,
          content,
          created_at: new Date().toISOString(),
          metadata: { temporary: true, finalized: false },
          owner_id: ownerId,
        },
      }),
    );
    this.store.dispatch(
      CopilotActions.sendMessage({
        clientId,
        conversationId,
        content,
        optimisticUserId,
        startedAt: new Date().toISOString(),
        clientGuid,
      }),
    );
  }
}

function uuid(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}
